import type { OutstandingOrderItem, OutstandingSessionRow, OutstandingTabDto } from '@gc/shared';

/**
 * The cashier's screen as ONE FLAT LIST, a row per unpaid thing.
 *
 * The screen used to group by party, one collapsible section each. The owner
 * asked for the flat table their previous software showed: every unpaid device
 * and every unpaid order in one grid, sorted newest first, with the party's name
 * repeated on each line instead of standing above it.
 *
 * Why that reads better at a till: the cashier is handed a name, and the thing
 * they need is every line under it, next to lines from everyone else so the
 * screen is one predictable shape. Grouping put the answer at a different
 * height on the screen every time.
 *
 * WHAT IS NOT LOST. Each row keeps its `tabId`, so settling is still per party
 * and a mixed selection still splits before anything is charged. The flattening
 * is presentation; the money rules underneath are untouched.
 *
 * Sessions and orders share a row shape on purpose. They are different things —
 * one has a clock, the other has a product count — but a cashier settling a
 * party wants them in one list in one order, and two tables would make "what
 * does this person owe" a question you answer twice.
 */

export interface OutstandingLine {
  /** Which half of the union this is; decides which cells have content. */
  kind: 'session' | 'order';
  /** `sessionId` or `billId` — unique across both, and what selection keys on. */
  id: string;
  tabId: string;
  /** Repeated on every row: the party's name is the column being scanned. */
  visitorName: string;
  customerAccountId: string | null;
  /** Orders carry a real invoice; a sitting has no bill until it is settled. */
  billNumber: string | null;
  deviceName: string | null;
  categoryName: string | null;
  /** Physical items on an order; null for a sitting, which sells no products. */
  productCount: number | null;
  startedAt: string;
  endedAt: string | null;
  /**
   * `POWERED_OFF` when the venue system stopped the sitting because its PC
   * went away (owner, 2026-10-14) — the "PC turned off" note under the end
   * time. Null for every other ending, and for an order, which does not run.
   */
  endedReason: OutstandingSessionRow['endedReason'];
  duration: string | null;
  hourlyPriceMd: string | null;
  numberOfRounds: number | null;
  roundPriceMd: string | null;
  totalMd: string;
  /**
   * Still running — the clock has not stopped.
   *
   * Decides the whole row's behaviour: a live sitting offers **End** (stop the
   * clock, leave the money owed) and a finished one offers **Pay**. Offering Pay
   * on a running device is how a party gets charged for time they are still
   * using, so this is never inferred from the total being present.
   */
  live: boolean;
  /**
   * An order's item lines, each carrying the exact figure paying just that
   * line costs (`owedMd`, allocated server-side). Null for a sitting.
   */
  items: OutstandingOrderItem[] | null;
  /**
   * Whether the server will split this bill by items — the row only expands
   * into tickable lines when it will. Always false for a sitting.
   */
  splittable: boolean;
}

/** Flatten the party-shaped response into the grid the screen draws. */
export function toOutstandingLines(tabs: readonly OutstandingTabDto[]): OutstandingLine[] {
  const lines: OutstandingLine[] = [];

  for (const tab of tabs) {
    for (const session of tab.sessions) {
      lines.push({
        kind: 'session',
        id: session.sessionId,
        tabId: tab.tabId,
        visitorName: tab.name,
        customerAccountId: tab.customerAccountId,
        billNumber: null,
        deviceName: session.deviceName,
        categoryName: session.categoryName || null,
        productCount: null,
        startedAt: session.startedAt,
        endedAt: session.endedAt,
        endedReason: session.endedReason,
        duration: session.duration,
        hourlyPriceMd: session.hourlyPriceMd,
        numberOfRounds: session.reservationType === 'ROUND' ? session.numberOfRounds : null,
        roundPriceMd: session.reservationType === 'ROUND' ? session.roundPriceMd : null,
        totalMd: session.totalMd,
        live: session.status === 'ACTIVE' || session.status === 'PAUSED',
        items: null,
        splittable: false,
      });
    }

    for (const order of tab.orders) {
      lines.push({
        kind: 'order',
        id: order.billId,
        tabId: tab.tabId,
        visitorName: tab.name,
        customerAccountId: tab.customerAccountId,
        billNumber: order.invoiceNumber,
        // Where it went — "the burger that went to PC-3" — which is the only
        // useful thing to put in a Device column for a plate of food.
        deviceName: order.destinationName,
        categoryName: null,
        productCount: order.itemCount,
        startedAt: order.placedAt,
        // An order does not run. Leaving these null is what keeps the grid
        // honest: a dash reads as "not applicable", where a zero would read as
        // "finished instantly" and a repeated `placedAt` as a duration of none.
        endedAt: null,
        endedReason: null,
        duration: null,
        hourlyPriceMd: null,
        numberOfRounds: null,
        roundPriceMd: null,
        totalMd: order.totalMd,
        live: false,
        items: order.items,
        splittable: order.splittable,
      });
    }
  }

  // Newest first: the party at the counter is nearly always the most recent
  // thing on the screen, and the previous software listed it this way.
  return lines.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

/** Every distinct party name on the screen, for the Visitor Name picker. */
export function visitorNamesOf(lines: readonly OutstandingLine[]): string[] {
  const seen = new Set<string>();
  for (const line of lines) seen.add(line.visitorName);
  // Sorted rather than in row order: the picker is a grid somebody's eye scans
  // alphabetically, not a history.
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** Every distinct device category present, for the category filter. */
export function categoriesOf(lines: readonly OutstandingLine[]): string[] {
  const seen = new Set<string>();
  for (const line of lines) {
    if (line.categoryName) seen.add(line.categoryName);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

export interface OutstandingFilters {
  /** Free text over the party name, device and bill number. */
  search: string;
  /** Exactly one party, chosen from the picker. Null means everyone. */
  visitorName: string | null;
  /** Null means every category — and always keeps orders, which have none. */
  category: string | null;
}

export const NO_FILTERS: OutstandingFilters = { search: '', visitorName: null, category: null };

/** Apply the filter bar. Pure, so the rules are pinned without a browser. */
export function filterOutstandingLines(
  lines: readonly OutstandingLine[],
  filters: OutstandingFilters,
): OutstandingLine[] {
  const needle = filters.search.trim().toLocaleLowerCase();

  return lines.filter((line) => {
    if (filters.visitorName !== null && line.visitorName !== filters.visitorName) return false;

    // A category filter must NOT swallow the party's food. An order has no
    // device and therefore no category, and dropping it would let a cashier
    // filter to "PCs", settle what they see, and hand back a customer who still
    // owes for two coffees.
    if (filters.category !== null && line.categoryName !== null) {
      if (line.categoryName !== filters.category) return false;
    }

    if (needle.length === 0) return true;
    return [line.visitorName, line.deviceName, line.billNumber].some(
      (field) => field !== null && field.toLocaleLowerCase().includes(needle),
    );
  });
}

/** The footer's two figures: how many lines, and what they come to. */
export function outstandingTotals(lines: readonly OutstandingLine[]): {
  count: number;
  totalMd: bigint;
} {
  let totalMd = 0n;
  for (const line of lines) totalMd += BigInt(line.totalMd);
  return { count: lines.length, totalMd };
}

/**
 * One party's block on the screen (owner request: "sectioned").
 *
 * Grouped by TAB and not by name, which is the whole subtlety. Two people can
 * share a name, and one person can have two tabs open — and settlement is per
 * tab, so a header reading "arin staff — 24,000" over two different parties
 * would quote a figure that no single Pay button can ever charge. One section
 * per tab means the subtotal on a header is always exactly what settling that
 * section costs.
 */
export interface OutstandingSection {
  tabId: string;
  /** The party's name, as it is printed on every row beneath. */
  visitorName: string;
  customerAccountId: string | null;
  lines: OutstandingLine[];
  count: number;
  totalMd: bigint;
  /** Any sitting in this party still running — the header says so. */
  liveCount: number;
}

/**
 * Cut the (already filtered) rows into one section per party.
 *
 * Section order follows the rows it is given, so the newest-first sort the flat
 * grid established still decides which party is at the top; within a section
 * the rows keep that order too. Nothing is re-sorted here, because a cashier
 * who scrolled to a party should not find it somewhere else after a poll.
 */
export function sectionOutstandingLines(
  lines: readonly OutstandingLine[],
): OutstandingSection[] {
  const byTab = new Map<string, OutstandingSection>();

  for (const line of lines) {
    let section = byTab.get(line.tabId);
    if (!section) {
      section = {
        tabId: line.tabId,
        visitorName: line.visitorName,
        customerAccountId: line.customerAccountId,
        lines: [],
        count: 0,
        totalMd: 0n,
        liveCount: 0,
      };
      byTab.set(line.tabId, section);
    }
    section.lines.push(line);
    section.count += 1;
    section.totalMd += BigInt(line.totalMd);
    if (line.live) section.liveCount += 1;
  }

  return [...byTab.values()];
}
